import React, { useState, useEffect } from 'react';
import clinicbookingService from '../../../../../services/clinicbooking.service';
import { Button, Form, Pagination, Row, Col, Modal } from 'react-bootstrap';
import Swal from '../../../../helpers/swal';
function CustomHours({ doctorid, clinicid, userid }) {
    const [customHours, setCustomHours] = useState([]);
    const [editingRow, setEditingRow] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalElements, setTotalElements] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [formData, setFormData] = useState({
        workDate: '',
        isOff: false,
        startTime: '10:00:00',
        endTime: '17:00:00',
        lunchFrom: '13:00:00',
        lunchTo: '13:30:00',
    });

    useEffect(() => {
        getHours(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const getHours = (pageNo, size) => {
        clinicbookingService
            .getCustomHours(clinicid, doctorid, pageNo - 1, size)
            .then((res) => {
                setCustomHours(res.data.content);
                setTotalPages(res.data.totalPages);
                setTotalElements(res.data.totalElements);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const handleAddNew = () => {
        setFormError('');
        setIsAdding(true);
        setEditingRow(null);
        setFormData({
            workDate: '',
            isOff: false,
            startTime: '10:00:00',
            endTime: '17:00:00',
            lunchFrom: '13:00:00',
            lunchTo: '13:30:00',
        });
        setShowModal(true);
    };

    const handleEdit = (row) => {
        setFormError('');
        setIsAdding(false);
        setEditingRow(row.id);
        setFormData({
            workDate: row.workDate,
            isOff: row.isOff,
            startTime: row.startTime,
            endTime: row.endTime,
            lunchFrom: row.lunchFrom,
            lunchTo: row.lunchTo,
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingRow(null);
        setIsAdding(false);
        setFormError('');
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-danger btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm me-2',
            },
            buttonsStyling: false,
        }).then((result) => {
            if (result.isConfirmed) {
                clinicbookingService
                    .deleteCustomHour(clinicid, doctorid, id)
                    .then(() => {
                        const isLastItemOnCurrentPage = customHours.length === 1;
                        const shouldGoToPreviousPage = isLastItemOnCurrentPage && currentPage > 1;
                        const newPage = shouldGoToPreviousPage ? currentPage - 1 : currentPage;

                        setCurrentPage(newPage);
                        getHours(newPage, pageSize);

                        Swal.fire('Deleted!', 'The custom hour has been deleted.', 'success');
                    })
                    .catch((err) => {
                        console.error('Error Deleting Row', err);
                        getHours(currentPage, pageSize);
                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: 'Failed to delete custom hour',
                        });
                    });
            }
        });
    };

    const handleSave = () => {
        if (!formData.workDate) {
            setFormError('Work date is required.');
            return;
        }

        setFormError('');
        setIsSaving(true);

        const dataToSave = { ...formData };
        const dateObject = new Date(dataToSave.workDate);
        const year = dateObject.getFullYear();
        const month = String(dateObject.getMonth() + 1).padStart(2, '0');
        const day = String(dateObject.getDate()).padStart(2, '0');
        dataToSave.workDate = `${year}-${month}-${day}`;
        dataToSave.userId = userid;
        dataToSave.doctorId = doctorid;
        dataToSave.clinicId = clinicid;

        clinicbookingService
            .addCutomHour(clinicid, doctorid, dataToSave)
            .then(() => {
                handleCloseModal();
                getHours(currentPage, pageSize);

                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `Custom hour ${isAdding ? 'added' : 'updated'} successfully`,
                });
            })
            .catch((err) => {
                console.error('Error saving custom hour', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: `Failed to ${isAdding ? 'add' : 'update'} custom hour`,
                });
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));
        const dayPart = date.getUTCDate();
        const monthPart = date.toLocaleString('default', { month: 'short' });
        const yearPart = date.getUTCFullYear();

        return `${dayPart} ${monthPart}, ${yearPart}`;
    };

    const workingCount = customHours.filter((row) => !row.isOff).length;
    const offCount = customHours.length - workingCount;

    return (
        <>
            <div className="mb-3">
                <h5 className="clinic-booking__title mb-1">
                    <i className="bi bi-calendar-event text-primary me-1"></i>
                    Custom Hours
                </h5>
                <p className="text-muted small mb-0">Date-specific schedule overrides for this doctor.</p>
            </div>

            <div className="notifications-panel">
                <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-calendar-plus"></i>
                        Overrides
                    </span>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-1"></i>
                        Add
                    </button>
                </div>

                <div className="notifications-panel__body">
                    <div className="clinic-booking-custom-hours-controls">
                        <div className="d-flex flex-wrap gap-2">
                            <span className="clinic-booking-stat clinic-booking-stat--total">{totalElements} total</span>
                            {customHours.length > 0 && (
                                <>
                                    <span className="clinic-booking-stat clinic-booking-stat--active">{workingCount} working</span>
                                    <span className="clinic-booking-stat clinic-booking-stat--inactive">{offCount} off</span>
                                </>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-2 small">
                            <span>Show</span>
                            <Form.Select
                                size="sm"
                                className="clinic-booking-page-size"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="15">15</option>
                            </Form.Select>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0 clinic-booking-custom-hours-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customHours.length === 0 ? (
                                    <tr>
                                        <td colSpan="5">
                                            <div className="clinic-booking-empty py-3">
                                                <i className="bi bi-calendar-x"></i>
                                                No custom hours found.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    customHours.map((row) => (
                                        <tr key={row.id}>
                                            <td>{formatDate(row.workDate)}</td>
                                            <td>
                                                {row.isOff ? (
                                                    <span className="clinic-booking-status-badge clinic-booking-status-badge--inactive">
                                                        <i className="bi bi-x-circle"></i>
                                                        Off
                                                    </span>
                                                ) : (
                                                    <span className="clinic-booking-status-badge clinic-booking-status-badge--active">
                                                        <i className="bi bi-check-circle"></i>
                                                        Working
                                                    </span>
                                                )}
                                            </td>
                                            <td>{row.isOff ? '—' : row.startTime}</td>
                                            <td>{row.isOff ? '—' : row.endTime}</td>
                                            <td className="text-end">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => handleEdit(row)}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                                <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row.id)}>
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalElements > 0 && (
                        <div className="clinic-booking-footer mt-3">
                            <div>
                                Showing {customHours.length} of {totalElements}
                            </div>
                            {totalPages > 1 && (
                                <Pagination size="sm" className="clinic-booking-custom-hours-pagination">
                                    <Pagination.First disabled={currentPage === 1} onClick={() => handlePageChange(1)} />
                                    <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />

                                    {[...Array(totalPages)].map((_, index) => {
                                        const page = index + 1;
                                        if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                            return (
                                                <Pagination.Item
                                                    key={page}
                                                    active={page === currentPage}
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </Pagination.Item>
                                            );
                                        }
                                        if (page === currentPage - 2 || page === currentPage + 2) {
                                            return <Pagination.Ellipsis key={page} />;
                                        }
                                        return null;
                                    })}

                                    <Pagination.Next
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    />
                                    <Pagination.Last disabled={currentPage === totalPages} onClick={() => handlePageChange(totalPages)} />
                                </Pagination>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Modal
                show={showModal}
                onHide={handleCloseModal}
                centered
                backdrop="static"
                keyboard={false}
                className="clinic-booking-custom-hour-modal"
            >
                <Modal.Header className="border-0 pb-0">
                    <Modal.Title className="clinic-booking-custom-hour-modal__title">
                        <i className={`bi ${isAdding ? 'bi-calendar-plus' : 'bi-pencil-square'} me-2`}></i>
                        {isAdding ? 'Add Custom Hour' : 'Edit Custom Hour'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-2">
                    <div className="notifications-alert-banner mb-3">
                        <i className="bi bi-info-circle"></i>
                        <span>
                            {formData.isOff
                                ? 'Mark this date as a day off. Working and lunch hours will be ignored.'
                                : 'Set a date-specific schedule override. Times use the 24-hour clock.'}
                        </span>
                    </div>

                    {formError && (
                        <div className="alert alert-danger border-0 py-2 px-3 small mb-3">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {formError}
                        </div>
                    )}

                    <Form>
                        <div className="clinic-booking-custom-hour-modal__section">
                            <div className="clinic-booking-custom-hour-modal__section-title">Date & Availability</div>
                            <Row className="g-3">
                                <Col md={7}>
                                    <Form.Group>
                                        <Form.Label className="notifications-form-label">Work Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            size="sm"
                                            name="workDate"
                                            value={formData.workDate}
                                            onChange={handleInputChange}
                                            disabled={isSaving}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={5}>
                                    <Form.Label className="notifications-form-label d-block">Availability</Form.Label>
                                    <div className="clinic-booking-custom-hour-modal__switch-row">
                                        <span>{formData.isOff ? 'Day Off' : 'Working Day'}</span>
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="custom-hour-is-off"
                                                name="isOff"
                                                checked={formData.isOff}
                                                onChange={handleInputChange}
                                                disabled={isSaving}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {!formData.isOff && (
                            <>
                                <div className="clinic-booking-custom-hour-modal__section">
                                    <div className="clinic-booking-custom-hour-modal__section-title">Working Hours</div>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="notifications-form-label">Start Time</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    size="sm"
                                                    step="1"
                                                    name="startTime"
                                                    value={formData.startTime}
                                                    onChange={handleInputChange}
                                                    disabled={isSaving}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="notifications-form-label">End Time</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    size="sm"
                                                    step="1"
                                                    name="endTime"
                                                    value={formData.endTime}
                                                    onChange={handleInputChange}
                                                    disabled={isSaving}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                <div className="clinic-booking-custom-hour-modal__section clinic-booking-custom-hour-modal__section--last">
                                    <div className="clinic-booking-custom-hour-modal__section-title">Lunch Break</div>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="notifications-form-label">Lunch From</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    size="sm"
                                                    step="1"
                                                    name="lunchFrom"
                                                    value={formData.lunchFrom}
                                                    onChange={handleInputChange}
                                                    disabled={isSaving}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="notifications-form-label">Lunch To</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    size="sm"
                                                    step="1"
                                                    name="lunchTo"
                                                    value={formData.lunchTo}
                                                    onChange={handleInputChange}
                                                    disabled={isSaving}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            </>
                        )}
                    </Form>
                </Modal.Body>

                <Modal.Footer className="border-0 pt-0">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCloseModal} disabled={isSaving}>
                        <i className="bi bi-x-lg me-1"></i>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-success btn-sm" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Saving…
                            </>
                        ) : (
                            <>
                                <i className="bi bi-floppy me-1"></i>
                                Save Custom Hour
                            </>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default CustomHours;
